/**
 * LUMINA · SynastryPage.jsx  v1.0  (Phase 86)
 * 合盘分析页 — 情侣/家庭/工作伙伴合盘
 */

import { useState } from "react";
import { track } from "../utils/track";
import { API } from "../constants/config";

const SYN_TYPES = [
  { key: "couple",   label: "💕 情侣合盘",   desc: "八字合婚+紫微夫妻宫+五行互补+冲突化解" },
  { key: "parenting", label: "👨‍👧 亲子合盘",   desc: "代际能量传递+教养功课+子女宫分析+边界建立" },
  { key: "family",   label: "🏠 家庭合盘",   desc: "全家五行格局+子女教养+长辈健康+能量场" },
  { key: "business",  label: "🤝 工作伙伴合盘", desc: "合伙人五行匹配+团队角色+合作吉凶" },
];

const DEFAULT_PERSON = { name: "", label: "", year: "", month: "", day: "", hour: "12", minute: "0", gender: "M" };

// ═══════════════════════════════════════════════════════════════════════════════
// Brand colors
// ═══════════════════════════════════════════════════════════════════════════════
const GD  = "#064E3B";
const GDL = "#0F766E";
const GDH = "#D4AF37";
const BG  = "#FDFBF7";
const CARD_BG = "#FFFFFF";

const scoreColor = (s) => {
  if (s >= 75) return "#059669";
  if (s >= 50) return GDH;
  return "#DC2626";
};


// ═══════════════════════════════════════════════════════════════════════════════
// Components
// ═══════════════════════════════════════════════════════════════════════════════

function PersonForm({ person, onChange, onRemove, index, canRemove }) {
  return (
    <div style={{
      background: CARD_BG,
      border: `1px solid rgba(212, 175, 55, 0.3)`,
      borderRadius: 12,
      padding: "16px 20px",
      marginBottom: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <strong style={{ color: GD, fontSize: 15 }}>👤 第{index + 1}人</strong>
        {canRemove && (
          <button onClick={() => onRemove(index)} style={{
            background: "none", border: "none", color: "#DC2626",
            cursor: "pointer", fontSize: 13, padding: "4px 8px",
          }}>
            ✕ 移除
          </button>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
        <InputRow label="姓名" value={person.name} onChange={v => onChange(index, "name", v)} placeholder="姓名" />
        <InputRow label="角色" value={person.label} onChange={v => onChange(index, "label", v)} placeholder="本人/伴侣/父亲..." />
        <InputRow label="出生年" value={person.year} onChange={v => onChange(index, "year", v)} placeholder="1978" />
        <InputRow label="月" value={person.month} onChange={v => onChange(index, "month", v)} placeholder="1-12" narrow />
        <InputRow label="日" value={person.day} onChange={v => onChange(index, "day", v)} placeholder="1-31" narrow />
        <InputRow label="时(0-23)" value={person.hour} onChange={v => onChange(index, "hour", v)} placeholder="0-23" narrow />
        <InputRow label="分" value={person.minute} onChange={v => onChange(index, "minute", v)} placeholder="0" narrow />
        <div>
          <label style={labelStyle}>性别</label>
          <select value={person.gender} onChange={e => onChange(index, "gender", e.target.value)}
            style={{ ...inputStyle, width: "100%" }}>
            <option value="M">男</option>
            <option value="F">女</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function InputRow({ label, value, onChange, placeholder, narrow }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ ...inputStyle, width: narrow ? "70px" : "100%" }} />
    </div>
  );
}

const labelStyle = { display: "block", fontSize: 12, color: "#6B7280", marginBottom: 2 };
const inputStyle = {
  padding: "6px 10px", border: "1px solid #D1D5DB", borderRadius: 6,
  fontSize: 13, outline: "none", background: BG,
};


// ═══════════════════════════════════════════════════════════════════════════════

function PairScoreCard({ ps, idx }) {
  const c = scoreColor(ps.overall);
  return (
    <div style={{
      background: CARD_BG,
      border: `1px solid ${c}33`,
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <strong style={{ fontSize: 16, color: GD }}>
          {ps.a_name} ↔ {ps.b_name}
        </strong>
        <span style={{
          fontSize: 28, fontWeight: 700, color: c,
          fontFamily: "Georgia, serif",
        }}>
          {ps.overall}
        </span>
      </div>

      {/* 三维度条形图 */}
      <div style={{ display: "grid", gridTemplateColumns: "60px 1fr 30px", gap: "4px 8px", marginBottom: 10 }}>
        {[
          ["八字", ps.bazi_score],
          ["五行", ps.wuxing_score],
          ["紫微", ps.ziwei_score],
        ].map(([label, score]) => (
          <React.Fragment key={label}>
            <span style={{ fontSize: 12, color: "#6B7280" }}>{label}</span>
            <div style={{
              height: 8, borderRadius: 4, background: "#F3F4F6",
              overflow: "hidden",
            }}>
              <div style={{
                width: `${score}%`, height: "100%",
                background: scoreColor(score),
                borderRadius: 4, transition: "width 0.5s",
              }} />
            </div>
            <span style={{ fontSize: 11, color: "#6B7280", textAlign: "right" }}>{score}</span>
          </React.Fragment>
        ))}
      </div>

      {/* 细节文本 */}
      {ps.bazi_detail && <DetailBlock label="八字分析" text={ps.bazi_detail} />}
      {ps.wuxing_detail && <DetailBlock label="五行互补" text={ps.wuxing_detail} />}
      {ps.ziwei_detail && <DetailBlock label="紫微对照" text={ps.ziwei_detail} />}

      {/* 冲突与和谐 */}
      {ps.harmony_points?.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <span style={{ fontSize: 11, color: "#059669" }}>✨ </span>
          {ps.harmony_points.map((p, i) => (
            <span key={i} style={{
              display: "inline-block", background: "#ECFDF5", color: "#065F46",
              padding: "2px 8px", borderRadius: 10, fontSize: 11,
              marginRight: 6, marginBottom: 4,
            }}>{p}</span>
          ))}
        </div>
      )}
      {ps.conflicts?.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <span style={{ fontSize: 11, color: "#DC2626" }}>⚠️ </span>
          {ps.conflicts.map((p, i) => (
            <span key={i} style={{
              display: "inline-block", background: "#FEF2F2", color: "#991B1B",
              padding: "2px 8px", borderRadius: 10, fontSize: 11,
              marginRight: 6, marginBottom: 4,
            }}>{p}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailBlock({ label, text }) {
  const [expanded, setExpanded] = useState(false);
  const short = text?.slice(0, 80) || "";
  const full = text || "";
  return (
    <div style={{ marginBottom: 4 }}>
      <span style={{ fontSize: 11, color: "#9CA3AF" }}>{label}: </span>
      <span style={{ fontSize: 12, color: "#4B5563" }}>
        {expanded ? full : short}
      </span>
      {full.length > 80 && (
        <button onClick={() => setExpanded(!expanded)} style={{
          background: "none", border: "none", color: GDH, cursor: "pointer",
          fontSize: 11, padding: 0, marginLeft: 4,
        }}>
          {expanded ? "收起" : "展开"}
        </button>
      )}
    </div>
  );
}

function GroupDynamics({ dynamics, stype }) {
  const eb = dynamics.element_balance || {};
  const maxV = Math.max(...Object.values(eb), 1);

  return (
    <div style={{
      background: CARD_BG,
      border: `1px solid rgba(212, 175, 55, 0.3)`,
      borderRadius: 12,
      padding: 16,
      marginBottom: 12,
    }}>
      <strong style={{ color: GD, fontSize: 15, display: "block", marginBottom: 10 }}>
        🌐 群体能量场
      </strong>

      {/* 元素分布条形图 */}
      <div style={{ marginBottom: 12 }}>
        {Object.entries(eb).map(([elem, val]) => (
          <div key={elem} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
            <span style={{ width: 20, fontSize: 12, color: "#6B7280" }}>{elem}</span>
            <div style={{
              flex: 1, height: 6, borderRadius: 3, background: "#F3F4F6",
            }}>
              <div style={{
                width: `${(val / maxV) * 100}%`, height: "100%",
                background: elemColor(elem),
                borderRadius: 3,
              }} />
            </div>
            <span style={{ fontSize: 11, color: "#6B7280", width: 35 }}>{val.toFixed(0)}%</span>
          </div>
        ))}
      </div>

      {dynamics.key_strength && (
        <p style={{ fontSize: 12, color: "#4B5563", margin: "4px 0" }}>
          最强: <strong style={{ color: GD }}>{dynamics.key_strength}</strong> · 最弱:{" "}
          <strong style={{ color: "#DC2626" }}>{dynamics.key_weakness}</strong>
        </p>
      )}

      {dynamics.advice && (
        <p style={{ fontSize: 12, color: "#6B7280", fontStyle: "italic", margin: "4px 0" }}>
          {dynamics.advice}
        </p>
      )}

      {dynamics.watch_out && (
        <p style={{
          fontSize: 12, color: "#DC2626", background: "#FEF2F2",
          padding: "6px 10px", borderRadius: 6, marginTop: 8,
        }}>
          ⚠ {dynamics.watch_out}
        </p>
      )}

      {dynamics.team_roles && (
        <div style={{ marginTop: 8 }}>
          {dynamics.team_roles.map((r, i) => (
            <p key={i} style={{ fontSize: 12, color: "#4B5563", margin: "2px 0" }}>
              {r}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function elemColor(e) {
  const m = { "木": "#059669", "火": "#DC2626", "土": "#D97706", "金": GDH, "水": "#2563EB" };
  return m[e] || "#9CA3AF";
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════════

export default function SynastryPage({ user, lang, setPage }) {
  const [people, setPeople] = useState([
    { ...DEFAULT_PERSON, label: "本人" },
    { ...DEFAULT_PERSON, label: "伴侣" },
  ]);
  const [synType, setSynType] = useState("couple");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleTypeChange = (key) => {
    setSynType(key);
    setResult(null);
    // Auto-set default labels per type
    const labels = {
      couple:    ["本人", "伴侣"],
      parenting: ["本人", "子女"],
      business:  ["本人", "合伙人"],
      family:    ["本人", "伴侣"],
    };
    const defaults = labels[key] || ["本人", "伴侣"];
    setPeople(prev => prev.map((p, i) => ({
      ...p,
      label: defaults[i] || p.label || "",
    })));
  };

  const addPerson = () => {
    if (people.length >= 6) return;
    setPeople([...people, { ...DEFAULT_PERSON }]);
  };

  const removePerson = (idx) => {
    if (people.length <= 2) return;
    setPeople(people.filter((_, i) => i !== idx));
  };

  const updatePerson = (idx, field, value) => {
    const updated = [...people];
    updated[idx] = { ...updated[idx], [field]: value };
    setPeople(updated);
  };

  const handleSubmit = async () => {
    setError("");
    setResult(null);

    // Validate
    for (let i = 0; i < people.length; i++) {
      const p = people[i];
      if (!p.name || !p.year || !p.month || !p.day) {
        setError(`第${i + 1}人信息不完整（姓名/年/月/日必填）`);
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("lumina_token");
      const payload = {
        people: people.map(p => ({
          name: p.name,
          label: p.label || p.name,
          year: parseInt(p.year),
          month: parseInt(p.month),
          day: parseInt(p.day),
          hour: parseInt(p.hour) || 0,
          minute: parseInt(p.minute) || 0,
          gender: p.gender,
        })),
        synastry_type: synType,
        lang: lang || "zh",
      };

      const res = await fetch(`${API}/audit/synastry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setResult(data);
      track("synastry_complete", { type: synType, people: people.length });
    } catch (e) {
      setError(e.message);
      track("synastry_error", { error: e.message });
    } finally {
      setLoading(false);
    }
  };

  // Determine page title
  const typeInfo = SYN_TYPES.find(t => t.key === synType) || SYN_TYPES[0];

  return (
    <div style={{
      maxWidth: 720, margin: "0 auto", padding: "40px 20px 80px",
      background: BG, minHeight: "100vh",
    }}>
      <h1 style={{
        fontSize: 24, color: GD, textAlign: "center", marginBottom: 4,
        fontFamily: "Georgia, serif",
      }}>
        合盘分析
      </h1>
      <p style={{ textAlign: "center", color: "#6B7280", fontSize: 13, marginBottom: 24 }}>
        八字合婚 · 五行互补 · 紫微对照 · 冲突化解
      </p>

      {/* 类型选择 */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, justifyContent: "center", flexWrap: "wrap" }}>
        {SYN_TYPES.map(t => (
          <button key={t.key} onClick={() => handleTypeChange(t.key)}
            style={{
              padding: "8px 16px", borderRadius: 20, border: `2px solid ${synType === t.key ? GDH : "#E5E7EB"}`,
              background: synType === t.key ? "#FEF3C7" : CARD_BG,
              color: synType === t.key ? GD : "#6B7280",
              cursor: "pointer", fontSize: 13, fontWeight: synType === t.key ? 600 : 400,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* 人物输入区域 */}
      <div style={{ marginBottom: 20 }}>
        {people.map((p, i) => (
          <PersonForm key={i} person={p} onChange={updatePerson} onRemove={removePerson}
            index={i} canRemove={people.length > 2} />
        ))}
        {people.length < 6 && (
          <button onClick={addPerson} style={{
            width: "100%", padding: "10px", border: `1px dashed ${GDH}`,
            borderRadius: 8, background: "transparent", color: GDH,
            cursor: "pointer", fontSize: 13,
          }}>
            ＋ 添加成员 ({people.length}/6)
          </button>
        )}
      </div>

      {/* 提交按钮 */}
      <button onClick={handleSubmit} disabled={loading} style={{
        width: "100%", padding: "14px", borderRadius: 10,
        background: loading ? "#9CA3AF" : GD,
        color: "#FFF", border: "none", fontSize: 16, fontWeight: 600,
        cursor: loading ? "not-allowed" : "pointer",
      }}>
        {loading ? "⏳ 计算中..." : `🔮 开始${typeInfo.label.replace(/[💕🏠🤝]/g, "").trim()}`}
      </button>

      {error && (
        <div style={{
          marginTop: 16, padding: "12px 16px", background: "#FEF2F2",
          borderRadius: 8, color: "#991B1B", fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* 结果区域 */}
      {result && (
        <div style={{ marginTop: 32 }}>
          {/* 综合分 */}
          <div style={{
            textAlign: "center", marginBottom: 24,
            padding: "20px", background: CARD_BG,
            border: `2px solid ${scoreColor(result.overall_score)}33`,
            borderRadius: 16,
          }}>
            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 4 }}>综合匹配度</div>
            <div style={{
              fontSize: 56, fontWeight: 700, color: scoreColor(result.overall_score),
              fontFamily: "Georgia, serif", lineHeight: 1,
            }}>
              {result.overall_score}
            </div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginTop: 4 }}>/100</div>
          </div>

          {/* 各对评分 */}
          {result.pair_scores?.map((ps, i) => (
            <PairScoreCard key={i} ps={ps} idx={i} />
          ))}

          {/* 群体动力 */}
          {result.group_dynamics && (
            <GroupDynamics dynamics={result.group_dynamics} stype={synType} />
          )}

          {/* AI 叙事 */}
          {result.narrative && (
            <div style={{
              background: CARD_BG,
              border: `1px solid ${GDH}44`,
              borderRadius: 12,
              padding: 20,
              marginTop: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <strong style={{ color: GD, fontSize: 15 }}>
                  🤖 AI 合盘解读
                </strong>
                {result.validation && (
                  <span style={{
                    fontSize: 11, padding: "3px 10px", borderRadius: 10,
                    background: result.validation.errors === 0 ? "#ECFDF5" : "#FEF2F2",
                    color: result.validation.errors === 0 ? "#059669" : "#DC2626",
                    border: `1px solid ${result.validation.errors === 0 ? "#05966933" : "#DC262633"}`,
                  }}>
                    {result.validation.errors === 0 ? "✅" : "❌"}
                    {" "}质量 {result.validation.errors === 0 ? "通过" : `${result.validation.errors}错`}
                    {result.validation.warnings > 0 && ` ${result.validation.warnings}警`}
                  </span>
                )}
              </div>
              <div style={{
                fontSize: 13, color: "#4B5563", lineHeight: 1.8,
                whiteSpace: "pre-wrap",
              }}>
                {result.narrative}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Need React for JSX
import React from "react";
